'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { type ReactElement } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { createStateContext } from 'react-use';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { type DatoBlock, type DatoModuleFormat } from '@/lib/datocms';
import { type ProgressCard, type ProgressFill } from './progress-card';
import { ProgressCardTabs } from './progress-card-tabs';

export type Progress = DatoBlock<{
  cards: ProgressCard[];
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  text?: StructuredTextGraphQlResponse;
  title?: string;
}>;

type Props = { data: Progress } & BoxProps;

export const [useModuleBackground, ModuleBackgroundProvider] =
  createStateContext<ProgressFill | null>(null);

export const Progress = forwardRef((props: Props, ref): ReactElement => {
  const { data, ...boxProps } = props;
  const {
    cards,
    headingProps,
    moduleFormat: [moduleFormat],
    text,
    title,
  } = data;

  return (
    <ModuleWrapper
      innerProps={{ direction: ['column', null, 'row'], gap: 4 }}
      moduleFormat={moduleFormat}
      outerProps={boxProps}
      ref={ref}
    >
      <Flex direction="column" flex="1" gap={2}>
        {title && (
          <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
            {title}
          </Text>
        )}
        {text && <StructuredContent color="text.inverse" data={text} />}
      </Flex>
      <ModuleBackgroundProvider initialValue={moduleFormat.background}>
        <ProgressCardTabs
          cards={cards}
          flex={[1, null, 2]}
          maxW={['100%', null, '552px']}
        />
      </ModuleBackgroundProvider>
    </ModuleWrapper>
  );
});
